import * as z from "zod";
import { type ManifestValidationResult, validateManifest } from "./_shared.js";

// ---------------------------------------------------------------------------
// Model identifier validation + normalization
// ---------------------------------------------------------------------------

/**
 * Anthropic model aliases recognized by Claude Code and the Claude Agent SDK.
 *
 * - `inherit` — use the parent agent's model (Claude Code only).
 * - `opus` / `sonnet` / `haiku` — auto-rolling pointer to the current top model
 *   in that family. Resolved by Claude Code / the Claude SDK at runtime.
 *
 * Use these in `agent.yaml` `model.preferred` for portability across Claude
 * Code and `claude-sdk`. Use a full ID (see {@link ANTHROPIC_FULL_ID_PATTERN})
 * when you need to pin to a specific Anthropic release.
 */
export const ANTHROPIC_MODEL_ALIASES = ["inherit", "opus", "sonnet", "haiku"] as const;
export type AnthropicModelAlias = (typeof ANTHROPIC_MODEL_ALIASES)[number];

/**
 * Anthropic model family segments recognized inside a full model identifier.
 *
 * This is the *ID* vocabulary, deliberately wider than
 * {@link ANTHROPIC_MODEL_ALIASES}: `fable` ships as a real model family
 * (`claude-fable-5`) but has no Claude Code rolling alias, so it belongs here
 * and not there.
 *
 * @since 1.5.0
 */
export const ANTHROPIC_MODEL_FAMILIES = ["opus", "sonnet", "haiku", "fable"] as const;
export type AnthropicModelFamily = (typeof ANTHROPIC_MODEL_FAMILIES)[number];

const FAMILY_ALT = ANTHROPIC_MODEL_FAMILIES.join("|");

/**
 * Family + version core shared by the full / Bedrock / Vertex patterns.
 *
 * Matches both the Claude 4+ "family-first" form (`opus-4-6`, `opus-5`) and the
 * legacy "version-first" form used by Claude 3.x and some OpenRouter aliases
 * (`3-5-sonnet`).
 *
 * The version is `\d+(?:[-.]\d+)*` — one *or more* numeric components. It used
 * to demand two or more, which rejected every current single-digit-major ID
 * (`claude-opus-5`, `claude-sonnet-5`, `claude-fable-5`). Those are structurally
 * indistinguishable from the older partial IDs the two-component rule was
 * written to catch (`claude-opus-4`), so accepting the current lineup
 * necessarily accepts those too. This validator is structural, not a catalog
 * check — a well-formed ID that no provider serves is caught at dispatch, not
 * here.
 */
const FAMILY_VERSION_CORE = `(?:(?:${FAMILY_ALT})-\\d+(?:[-.]\\d+)*|\\d+(?:[-.]\\d+)*-(?:${FAMILY_ALT}))`;

/**
 * Regex matching a full Anthropic model identifier, optionally provider-prefixed.
 *
 * Examples that match (family-first):
 * - `claude-opus-5`
 * - `claude-sonnet-5`
 * - `claude-fable-5`
 * - `claude-opus-4-6`
 * - `claude-haiku-4-5-20251001`
 * - `anthropic/claude-sonnet-4.6` (OpenRouter form)
 *
 * Examples that match (version-first / legacy):
 * - `claude-3-5-sonnet-20241022`
 * - `anthropic/claude-3.7-sonnet`
 *
 * Still rejected: a bare family (`claude-opus`), a non-Claude vendor
 * (`gpt-4o`), an unknown family (`claude-turbo-5`).
 */
export const ANTHROPIC_FULL_ID_PATTERN = new RegExp(
  `^(?:[a-z0-9-]+\\/)?claude-${FAMILY_VERSION_CORE}(?:-\\d{8})?$`,
);

/**
 * Regex matching an Amazon Bedrock-form Anthropic model identifier: an
 * optional region/scope prefix (`us.` / `eu.` / `apac.` / `global.`), the
 * `anthropic.` vendor segment, the same family/version core as
 * {@link ANTHROPIC_FULL_ID_PATTERN}, plus Bedrock's optional
 * `-v<major>[:<minor>]` suffix.
 *
 * Examples that match:
 * - `anthropic.claude-3-5-sonnet-20241022-v2:0`
 * - `us.anthropic.claude-opus-4-6-v1:0`
 * - `anthropic.claude-sonnet-4-5-20250929-v1:0`
 *
 * Does NOT match `anthropic.gpt-4` (not a Claude family) or a bare
 * `us.claude-…` (the `anthropic.` segment is required).
 *
 * @since 1.3.0
 */
export const BEDROCK_MODEL_ID_PATTERN = new RegExp(
  `^(?:(?:us|eu|apac|global)\\.)?anthropic\\.claude-${FAMILY_VERSION_CORE}(?:-\\d{8})?(?:-v\\d+(?::\\d+)?)?$`,
);

/**
 * Regex matching a Google Vertex AI publisher-form Anthropic identifier —
 * a first-party ID pinned with an `@YYYYMMDD` date suffix instead of the
 * native `-YYYYMMDD` (e.g. `claude-sonnet-4-5@20250929`).
 *
 * @since 1.3.0
 */
export const VERTEX_MODEL_ID_PATTERN = new RegExp(`^claude-${FAMILY_VERSION_CORE}@\\d{8}$`);

/**
 * Zod schema accepting a Claude Code alias, a full Anthropic ID, a Bedrock-form
 * ID (see {@link BEDROCK_MODEL_ID_PATTERN}), or a Vertex `@date`-pinned ID
 * (see {@link VERTEX_MODEL_ID_PATTERN}).
 *
 * Anything else fails validation. Use {@link normalizeAnthropicModel} to coerce
 * unrecognized values to a safe alias instead of failing, and
 * {@link modelIdSchemaForCloud} for free-form IDs under `cloud: gateway`.
 */
export const ModelIdSchema = z.union([
  z.enum(ANTHROPIC_MODEL_ALIASES),
  z
    .string()
    .regex(
      ANTHROPIC_FULL_ID_PATTERN,
      "Invalid Anthropic model ID. Use an alias (opus | sonnet | haiku | inherit), a full ID (e.g. claude-opus-5, claude-sonnet-4-6), a Bedrock ID (e.g. us.anthropic.claude-opus-4-6-v1:0), or a Vertex ID (e.g. claude-sonnet-4-5@20250929).",
    ),
  z.string().regex(BEDROCK_MODEL_ID_PATTERN),
  z.string().regex(VERTEX_MODEL_ID_PATTERN),
]);

/**
 * Contextual model-ID schema: `cloud: gateway` routes through an arbitrary
 * proxy whose model namespace we cannot know, so any non-empty string is
 * accepted there; every other cloud (or none) uses {@link ModelIdSchema}.
 *
 * @since 1.3.0
 */
export function modelIdSchemaForCloud(cloud?: string): z.ZodType<string> {
  return cloud === "gateway" ? z.string().min(1) : (ModelIdSchema as z.ZodType<string>);
}

/**
 * Normalize an arbitrary model string to a safe Claude Code value.
 *
 * - If `input` is a recognized alias or a full / Bedrock-form / Vertex-form ID,
 *   returns it unchanged.
 * - If `input` looks like `claude-<family>-...` but doesn't match any full-ID
 *   regex (e.g. the broken `claude-opus-4`), returns the family alias.
 * - Otherwise returns `fallback` (default `"sonnet"` — Claude Code's default).
 *
 * Used by renderers that target Claude Code / the Claude SDK to coerce stale
 * or malformed model fields into a working alias rather than propagating a
 * silent runtime failure. All full-pattern checks run BEFORE the family
 * fallback so a valid Bedrock/Vertex ID is never downgraded to an alias.
 */
export function normalizeAnthropicModel(
  input: string | undefined | null,
  fallback: AnthropicModelAlias = "sonnet",
): string {
  if (!input) return fallback;
  if ((ANTHROPIC_MODEL_ALIASES as readonly string[]).includes(input)) return input;
  if (ANTHROPIC_FULL_ID_PATTERN.test(input)) return input;
  if (BEDROCK_MODEL_ID_PATTERN.test(input) || VERTEX_MODEL_ID_PATTERN.test(input)) return input;
  // Family fallback is restricted to the rolling *aliases* — `fable` has no
  // Claude Code alias, so a malformed `claude-fable-*` falls through to
  // `fallback` rather than to an alias the runtime would reject.
  const familyMatch = input.match(/claude-(opus|sonnet|haiku)/i);
  if (familyMatch) return familyMatch[1]!.toLowerCase() as AnthropicModelAlias;
  return fallback;
}

const ModelConfigSchema = z.looseObject({
  preferred: ModelIdSchema.optional(),
  fallback: z.array(ModelIdSchema).optional(),
  constraints: z
    .looseObject({
      temperature: z.number().optional(),
      max_tokens: z.number().optional(),
    })
    .optional(),
});

// ---------------------------------------------------------------------------
// Composition binding types (Task 1.7)
// ---------------------------------------------------------------------------

export const BindingValues = ["discoverable", "inline-live", "inline-snapshot"] as const;
export const BindingSchema = z.enum(BindingValues);
export type Binding = z.infer<typeof BindingSchema>;

/**
 * A single composition item within an agent's `composes:` field.
 *
 * - `kind`: the asset kind being composed (skill, connector, ruleset, etc.)
 * - `ref`: asset ref (e.g. '@skaile/gmail@^1')
 * - `instance`: optional Library Instance ref to reuse (e.g. 'library://personal/instances/uuid')
 * - `binding`: how the asset integrates into the agent runtime; defaults per-kind
 */
export const AgentComposeItemSchema = z.object({
  kind: z.string().min(1),
  ref: z.string().min(1),
  instance: z.string().optional(),
  binding: BindingSchema.optional(),
});

export type AgentComposeItem = z.infer<typeof AgentComposeItemSchema>;

// ---------------------------------------------------------------------------
// Agent manifest
// ---------------------------------------------------------------------------

/**
 * Declared tool restrictions for an agent.
 *
 * `allowed` is an allowlist: when non-empty, the agent may invoke only these
 * tools. `denied` is a denylist applied on top. An absent block (or two empty
 * arrays) means "no restriction" — the backend's full default toolset.
 *
 * Tool names are written in the canonical Claude Code vocabulary (`Read`,
 * `Write`, `Bash`, `WebSearch`, …). Each driver maps them onto its own
 * runtime: `claude-sdk` passes them through as `allowedTools` /
 * `disallowedTools`; the omp driver translates them to omp's built-in tool
 * names and passes `--tools`. A name a backend does not recognize is dropped
 * for that backend rather than failing the manifest, because the same manifest
 * is expected to run on more than one backend.
 *
 * This was previously an undeclared field that survived only because
 * {@link AgentManifestSchema} is a loose object — so `AgentManifest.tools` was
 * typed `unknown` and every consumer read it through a cast.
 *
 * @since 1.5.0
 */
export const AgentToolsSchema = z.looseObject({
  /** Tool names the agent may invoke. Empty or absent means "no allowlist". */
  allowed: z.array(z.string().min(1)).optional(),
  /** Tool names the agent must not invoke. Applied after `allowed`. */
  denied: z.array(z.string().min(1)).optional(),
});

export type AgentTools = z.infer<typeof AgentToolsSchema>;

export const AgentManifestSchema = z.looseObject({
  spec_version: z.string().optional(),
  name: z.string().min(1),
  version: z.string().optional(),
  description: z.string().min(1),
  extends: z.string().optional(),

  model: ModelConfigSchema.optional(),
  tools: AgentToolsSchema.optional(),
  skills: z.array(z.string()).optional(),
  delegation: z.looseObject({ mode: z.string() }).optional(),
  runtime: z
    .looseObject({
      max_turns: z.number().optional(),
      timeout: z.number().optional(),
    })
    .optional(),

  author: z.string().optional(),
  license: z.string().optional(),
  tags: z.array(z.string()).optional(),
  keywords: z.array(z.string()).optional(),

  // v2 composition (Task 1.7)
  composes: z.array(AgentComposeItemSchema).optional(),

  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type AgentManifest = z.infer<typeof AgentManifestSchema>;

export function validateAgent(data: unknown): ManifestValidationResult<AgentManifest> {
  return validateManifest(AgentManifestSchema, data);
}
